import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { csrfHeaders } from '../utils/csrf';
import './TagTester.css';

interface MacroPreview {
  macros: string[];
  expanded: Record<string, string>;
}

export function TagTesterIntake(): JSX.Element {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [label, setLabel] = useState('');
  const [scanMode, setScanMode] = useState<'quick' | 'full'>('quick');
  const [detectedType, setDetectedType] = useState<string>('');
  const [macroPreview, setMacroPreview] = useState<MacroPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Auto-detect input type
  const detectInputType = useCallback((text: string): string => {
    if (!text.trim()) return '';

    const trimmed = text.trim();

    if (trimmed.toLowerCase().startsWith('<script')) return 'script';
    if (trimmed.toLowerCase().startsWith('<iframe')) return 'iframe';
    if (trimmed.toLowerCase().startsWith('<?xml') || trimmed.toLowerCase().startsWith('<vast')) {
      return 'vast_xml';
    }

    // Check for URL patterns
    const urlPattern = /^https?:\/\/.+/i;
    if (urlPattern.test(trimmed)) {
      if (trimmed.toLowerCase().endsWith('.xml')) return 'vast_url';
      return 'url';
    }

    return 'raw';
  }, []);

  // Update detected type on input change
  useEffect(() => {
    const type = detectInputType(input);
    setDetectedType(type);
  }, [input, detectInputType]);

  // Debounced macro preview fetch
  useEffect(() => {
    if (!input.trim()) {
      setMacroPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const response = await fetch(apiUrl('/tag-scans/macros/preview'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...csrfHeaders(),
          },
          body: JSON.stringify({ rawCode: input }),
        });

        if (response.ok) {
          const data = await response.json();
          setMacroPreview(data);
        }
      } catch (err) {
        // Silent fail for preview - non-critical
        console.warn('Macro preview failed', err);
      } finally {
        setPreviewLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [input]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim()) {
      setError('Please paste or enter tag code');
      return;
    }

    if (!detectedType) {
      setError('Unable to detect input type. Please check your input.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(apiUrl('/tag-scans'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeaders(),
        },
        body: JSON.stringify({
          rawCode: input,
          
          mode: scanMode,
          label: label || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initiate scan');
      }

      const data = await response.json();

      if (data.success && data.data?.scanId) {
        navigate(`/tag-tester/${data.data.scanId}`);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="tag-tester-intake">
      <div className="intake-container">
        <div className="intake-main">
          <div className="intake-header">
            <h1>Third Party Tag Tester</h1>
            <p>Paste your tag code to scan for security risks, tracking, and policy violations</p>
          </div>

          <form onSubmit={handleSubmit} className="intake-form">
            {/* Input Area */}
            <div className="form-section">
              <div className="input-header">
                <label htmlFor="tag-input">Tag Code</label>
                {detectedType && (
                  <div className={`detection-badge detection-${detectedType}`}>
                    {detectedType === 'vast_url' ? 'VAST URL' :
                     detectedType === 'vast_xml' ? 'VAST XML' :
                     detectedType.charAt(0).toUpperCase() + detectedType.slice(1)}
                  </div>
                )}
              </div>
              <textarea
                id="tag-input"
                className="tag-input-area"
                placeholder="Paste script tag, iframe, URL, or raw HTML..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Label Input */}
            <div className="form-section">
              <label htmlFor="label-input">Label (Optional)</label>
              <input
                id="label-input"
                type="text"
                className="label-input"
                placeholder="e.g., Analytics Tag v2"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Scan Mode Toggle */}
            <div className="form-section">
              <label>Scan Mode</label>
              <div className="scan-mode-toggle">
                <button
                  type="button"
                  className={`mode-button ${scanMode === 'quick' ? 'active' : ''}`}
                  onClick={() => setScanMode('quick')}
                  disabled={loading}
                >
                  <span className="mode-icon">⚡</span>
                  Quick Scan
                  <span className="mode-time">~20s</span>
                </button>
                <button
                  type="button"
                  className={`mode-button ${scanMode === 'full' ? 'active' : ''}`}
                  onClick={() => setScanMode('full')}
                  disabled={loading}
                >
                  <span className="mode-icon">🔍</span>
                  Full Scan
                  <span className="mode-time">~45s</span>
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && <div className="error-message">{error}</div>}

            {/* Submit Button */}
            <button
              type="submit"
              className="scan-button"
              disabled={loading || !input.trim()}
            >
              {loading ? 'Scanning...' : 'Scan Tag'}
            </button>
          </form>
        </div>

        {/* Macro Preview Panel */}
        {input.trim() && (
          <div className="macro-preview-panel">
            <div className="panel-header">
              <h3>Macro Preview</h3>
              {previewLoading && <div className="preview-spinner" />}
            </div>

            {macroPreview && macroPreview.macros.length > 0 ? (
              <div className="macro-content">
                <div className="macro-list">
                  {macroPreview.macros.map((macro, idx) => (
                    <div key={idx} className="macro-item">
                      <div className="macro-name">{macro}</div>
                      {macroPreview.expanded[macro] && (
                        <div className="macro-expansion">
                          {macroPreview.expanded[macro]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="macro-empty">
                {previewLoading ? 'Analyzing macros...' : 'No macros detected'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
